import { useEffect, useState } from 'react'

/**
 * Deliberate responsive modes for the worker client.
 *
 * `phone` gets the Android-like worker shell; `tablet` gets an adaptive layout
 * rather than an immediate fall back to the desktop Admin UX; `desktop` keeps
 * the wide worker layout with the same features and routes.
 */
export type ViewportMode = 'phone' | 'tablet' | 'desktop'

export const PHONE_MAX_WIDTH = 767
export const TABLET_MAX_WIDTH = 1099

export function resolveViewportMode(width: number): ViewportMode {
  if (width <= PHONE_MAX_WIDTH) return 'phone'
  if (width <= TABLET_MAX_WIDTH) return 'tablet'
  return 'desktop'
}

export function useViewportMode(): ViewportMode {
  const [mode, setMode] = useState<ViewportMode>(() =>
    typeof window === 'undefined' ? 'desktop' : resolveViewportMode(window.innerWidth))

  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    const sync = () => setMode(resolveViewportMode(window.innerWidth))
    sync()
    window.addEventListener('resize', sync)
    window.addEventListener('orientationchange', sync)
    return () => {
      window.removeEventListener('resize', sync)
      window.removeEventListener('orientationchange', sync)
    }
  }, [])

  return mode
}

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window === 'undefined' || typeof window.matchMedia !== 'function'
      ? false
      : window.matchMedia(query).matches)

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined
    const list = window.matchMedia(query)
    const sync = () => setMatches(list.matches)
    sync()
    list.addEventListener('change', sync)
    return () => list.removeEventListener('change', sync)
  }, [query])

  return matches
}
